import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Plus, Trash, PencilSimple, Check, X, MagnifyingGlass, FilePdf, FileXls, Tag } from "@phosphor-icons/react";
import { usePeriodFilter } from "./PeriodFilter";

const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

const downloadFile = (url, token) => {
  const a = document.createElement("a");
  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => r.blob())
    .then(blob => { a.href = URL.createObjectURL(blob); a.download = ""; a.click(); });
};

export function ExpensesPanel({ year, month, categories, onCategoriesChanged }) {
  const { token, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [newCat, setNewCat] = useState("");
  const [showNewCat, setShowNewCat] = useState(false);
  const [form, setForm] = useState({ category_id: "", category_name: "", amount: "", description: "", date: new Date().toISOString().slice(0, 10) });

  const load = useCallback(async () => {
    try {
      const params = { year };
      if (month) params.month = month;
      const { data } = await axios.get(`${API}/admin/expenses`, { params, headers: { Authorization: `Bearer ${token}` } });
      setItems(data);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [year, month, token]);

  useEffect(() => { load(); }, [load]);

  const handleCategoryChange = (catId) => {
    const cat = categories.find(c => c.id === catId);
    setForm(p => ({ ...p, category_id: catId, category_name: cat?.name || "" }));
  };

  const handleAddExpenseCategory = async () => {
    if (!newCat.trim()) return;
    try {
      const { data: cat } = await axios.post(`${API}/admin/expense-categories`, { name: newCat.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      setNewCat(""); setShowNewCat(false);
      if (onCategoriesChanged) onCategoriesChanged();
      handleCategoryChange(cat.id);
      toast.success("Categoria agregada");
    } catch { toast.error("Error"); }
  };

  const handleSave = async () => {
    if (!form.category_id || !form.amount || !form.date) { toast.error("Completa los campos requeridos"); return; }
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (editId) {
        await axios.put(`${API}/admin/expenses/${editId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Gasto actualizado");
      } else {
        await axios.post(`${API}/admin/expenses`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Gasto registrado");
      }
      resetForm(); load();
    } catch { toast.error("Error al guardar"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este gasto?")) return;
    try { await axios.delete(`${API}/admin/expenses/${id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Eliminado"); load(); }
    catch { toast.error("Error"); }
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setForm({ category_id: item.category_id, category_name: item.category_name || "", amount: item.amount, description: item.description, date: item.date });
    setShowForm(true);
  };

  const resetForm = () => {
    setEditId(null); setShowForm(false);
    setForm({ category_id: "", category_name: "", amount: "", description: "", date: new Date().toISOString().slice(0, 10) });
  };

  const searchFiltered = items.filter(i => i.category_name?.toLowerCase().includes(search.toLowerCase()) || i.description?.toLowerCase().includes(search.toLowerCase()));
  const { filtered, PeriodFilter } = usePeriodFilter(searchFiltered, year);
  const total = filtered.reduce((s, i) => s + (i.amount || 0), 0);

  return (
    <div className="space-y-4" data-testid="expenses-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar..." className="pl-9 pr-3 py-2 border rounded-lg text-sm w-64" data-testid="expense-search" />
          </div>
          <span className="text-sm font-semibold text-red-700 bg-red-50 px-3 py-1.5 rounded-lg">Total: {fmt(total)}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { resetForm(); setShowForm(true); }} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 flex items-center gap-1.5" data-testid="expense-add-btn">
            <Plus size={16} /> Nuevo Gasto
          </button>
          {isAdmin && <button onClick={() => downloadFile(`${API}/admin/expenses/pdf?${year ? `year=${year}` : ""}${month ? `&month=${month}` : ""}`, token)} className="px-3 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 flex items-center gap-1.5" data-testid="expense-pdf-btn">
            <FilePdf size={16} /> PDF
          </button>}
          {isAdmin && <button onClick={() => downloadFile(`${API}/admin/expenses/excel?${year ? `year=${year}` : ""}${month ? `&month=${month}` : ""}`, token)} className="px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-100 flex items-center gap-1.5" data-testid="expense-excel-btn">
            <FileXls size={16} /> Excel
          </button>}
        </div>
      </div>

      <PeriodFilter />

      {showForm && (
        <div className="bg-white rounded-xl border p-5 space-y-3" data-testid="expense-form">
          <h3 className="font-semibold text-slate-700">{editId ? "Editar Gasto" : "Nuevo Gasto"}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Categoria *</label>
              <div className="flex gap-1 mt-0.5">
                <select value={form.category_id} onChange={e => handleCategoryChange(e.target.value)} className="flex-1 px-3 py-2 border rounded-lg text-sm" data-testid="expense-category">
                  <option value="">Seleccionar...</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button type="button" onClick={() => setShowNewCat(!showNewCat)} className="px-2 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100" title="Agregar categoria" data-testid="expense-add-cat-btn">
                  <Tag size={16} />
                </button>
              </div>
              {showNewCat && (
                <div className="flex gap-1 mt-1">
                  <input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="Nueva categoria..." className="flex-1 px-2 py-1.5 border rounded-lg text-xs" onKeyDown={e => e.key === "Enter" && handleAddExpenseCategory()} data-testid="expense-new-cat-input" />
                  <button onClick={handleAddExpenseCategory} className="px-2 py-1 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700" data-testid="expense-new-cat-save">OK</button>
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Monto *</label>
              <input type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="expense-amount" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Fecha *</label>
              <input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="expense-date" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Descripcion</label>
              <input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="expense-desc" />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={handleSave} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-1" data-testid="expense-save-btn">
              <Check size={16} /> {editId ? "Actualizar" : "Guardar"}
            </button>
            <button onClick={resetForm} className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm hover:bg-slate-200 flex items-center gap-1">
              <X size={16} /> Cancelar
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Fecha</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Categoria</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Descripcion</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500">Monto</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.map(item => {
              const cat = categories.find(c => c.id === item.category_id);
              return (
                <tr key={item.id} className="hover:bg-slate-50" data-testid={`expense-row-${item.id}`}>
                  <td className="px-4 py-3 text-slate-600">{item.date}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: (cat?.color || "#6B7280") + "15", color: cat?.color || "#6B7280" }}>
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cat?.color || "#6B7280" }} />
                      {item.category_name}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{item.description}</td>
                  <td className="px-4 py-3 text-right font-semibold text-red-700">{fmt(item.amount)}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex justify-center gap-1">
                      <button onClick={() => startEdit(item)} className="p-1.5 hover:bg-blue-50 rounded text-blue-600"><PencilSimple size={14} /></button>
                      {isAdmin && <button onClick={() => handleDelete(item.id)} className="p-1.5 hover:bg-red-50 rounded text-red-500"><Trash size={14} /></button>}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No hay gastos registrados</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
